import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../config";
import {
  FileDown,
  PhoneCall,
  BarChart3,
  CheckCircle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  CreditCard,
  Wallet,
} from "lucide-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";

const AgentExpenseTable = ({ dateRange }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [totals, setTotals] = useState({
    marketing_spent: 0,
    total_calls: 0,
    MCO: 0,
    bookings: 0,
    gateway_cost: 0,
    salary_share: 0,
    admin_share: 0,
    gross_margin: 0,
  });

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return;

    const addHoursUTC = (isoString, hours) => {
      const date = new Date(
        isoString.endsWith("Z") ? isoString : isoString + "Z"
      );
      date.setUTCHours(date.getUTCHours() + hours);
      return date.toISOString();
    };

    const startDateAdjusted = addHoursUTC(dateRange.startDate, 5);
    const endDateAdjusted = addHoursUTC(dateRange.endDate, 5);

    const fetchSummary = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}get_marketing_summary_agentwise.php?startDate=${encodeURIComponent(
            startDateAdjusted
          )}&endDate=${encodeURIComponent(endDateAdjusted)}`,
          { credentials: "include" }
        );

        const json = await res.json();

        if (json.status === "success") {
          const data = json.summary || [];

          const processed = data.map((row) => {
            const mco = Number(row.MCO || 0);
            const mkt = Number(row.marketing_spent || 0);
            const gateway = Number(row.gateway_cost || 0);
            const salary = Number(row.salary_share || 0);
            const admin = Number(row.admin_share || 0);

            return {
              ...row,
              gross_margin: mco - mkt - gateway - salary - admin,
            };
          });

          setSummaryData(processed);

          const totalRow = processed.find((r) => r.agent_name === "Total");
          if (totalRow) setTotals(totalRow);
        } else {
          setSummaryData([]);
        }
      } catch (err) {
        console.error("Failed to fetch agent summary:", err);
        setSummaryData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [dateRange]);

  const averages = (() => {
    if (!summaryData.length) return {};

    const validRows = summaryData.filter((r) => r.agent_name !== "Total");

    const avg = (field, transform) => {
      const values = validRows.map((row) =>
        transform ? transform(row) : Number(row[field]) || 0
      );
      return values.reduce((a, b) => a + b, 0) / values.length || 0;
    };

    return {
      marketing_spent: avg("marketing_spent"),
      MCO: avg("MCO"),
      bookings: avg("bookings"),
      gateway_cost: avg("gateway_cost"),
      conversion: avg(null, (r) =>
        r.total_calls > 0 ? (r.bookings / r.total_calls) * 100 : 0
      ),
      revenueRatio: avg(null, (r) =>
        r.marketing_spent > 0 ? r.MCO / r.marketing_spent : 0
      ),
      gross_margin: avg("gross_margin"),
    };
  })();

  const getColorClass = (value, avg, reverse = false) => {
    if (!avg || avg === 0) return "text-slate-300";
    const ratio = value / avg;

    if (reverse) {
      if (ratio < 0.9) return "text-emerald-400 font-semibold";
      if (ratio > 1.1) return "text-red-400 font-semibold";
    } else {
      if (ratio > 1.1) return "text-emerald-400 font-semibold";
      if (ratio < 0.9) return "text-red-400 font-semibold";
    }
    return "text-slate-300";
  };

  const getTrendIcon = (value, avg, reverse = false) => {
    if (!avg || avg === 0) return null;
    const ratio = value / avg;

    if (reverse) {
      if (ratio < 0.9) return <TrendingUp className="inline w-3 h-3 ml-1" />;
      if (ratio > 1.1) return <TrendingDown className="inline w-3 h-3 ml-1" />;
    } else {
      if (ratio > 1.1) return <TrendingUp className="inline w-3 h-3 ml-1" />;
      if (ratio < 0.9) return <TrendingDown className="inline w-3 h-3 ml-1" />;
    }
    return <Minus className="inline w-3 h-3 ml-1 opacity-30" />;
  };

  const exportToExcel = async () => {
    if (!summaryData.length) return alert("No data to export.");

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Agent Summary");

    const headers = [
      "Agent Name",
      "Marketing Spent",
      "Calls",
      "Gateway Cost",
      "Salary",
      "Admin",
      "MCO",
      "Bookings",
      "Conversion (%)",
      "Revenue Ratio",
      "Gross Margin",
    ];

    sheet.addRow(headers);

    summaryData.forEach((row) => {
      const conversion =
        row.total_calls > 0
          ? ((row.bookings / row.total_calls) * 100).toFixed(2) + "%"
          : "0%";

      sheet.addRow([
        row.agent_name,
        row.marketing_spent,
        row.total_calls,
        row.gateway_cost,
        row.salary_share,
        row.admin_share,
        row.MCO,
        row.bookings,
        conversion,
        row.marketing_spent > 0
          ? (row.MCO / row.marketing_spent).toFixed(2)
          : "0.00",
        row.gross_margin,
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `AgentSummary_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedData = (() => {
    const totalRow = summaryData.find((r) => r.agent_name === "Total");
    const rest = summaryData.filter((r) => r.agent_name !== "Total");

    const getValue = (row, field) => {
      switch (field) {
        case "conversion":
          return row.total_calls > 0
            ? (row.bookings / row.total_calls) * 100
            : 0;
        case "revenueRatio":
          return row.marketing_spent > 0 ? row.MCO / row.marketing_spent : 0;
        case "gross_margin":
          return Number(row.gross_margin) || 0;
        default:
          return Number(row[field]) || 0;
      }
    };

    if (!sortField) return totalRow ? [...rest, totalRow] : rest;

    const sorted = [...rest].sort((a, b) => {
      const valA = getValue(a, sortField);
      const valB = getValue(b, sortField);
      return sortOrder === "asc" ? valA - valB : valB - valA;
    });

    return totalRow ? [...sorted, totalRow] : sorted;
  })();

  // Summary cards configuration
  const summaryCards = [
    {
      label: "Total Marketing Spend",
      value: `$${(totals.marketing_spent || 0).toFixed(2)}`,
      icon: DollarSign,
      gradient: "from-emerald-500/20 to-emerald-600/5",
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-400",
      borderColor: "border-emerald-500/30",
    },
    {
      label: "Total Calls",
      value: totals.total_calls || 0,
      icon: PhoneCall,
      gradient: "from-blue-500/20 to-blue-600/5",
      iconBg: "bg-blue-500/10",
      iconColor: "text-blue-400",
      borderColor: "border-blue-500/30",
    },
    {
      label: "Total Revenue",
      value: `$${(totals.MCO || 0).toFixed(2)}`,
      icon: BarChart3,
      gradient: "from-cyan-500/20 to-cyan-600/5",
      iconBg: "bg-cyan-500/10",
      iconColor: "text-cyan-400",
      borderColor: "border-cyan-500/30",
    },
    {
      label: "Total Bookings",
      value: totals.bookings || 0,
      icon: CheckCircle,
      gradient: "from-purple-500/20 to-purple-600/5",
      iconBg: "bg-purple-500/10",
      iconColor: "text-purple-400",
      borderColor: "border-purple-500/30",
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-800/50 rounded-xl" />
            ))}
          </div>
          <div className="h-96 bg-slate-800/50 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Agent Productivity Summary{" "}
            <span className="text-slate-400 text-xl">(Agent-wise)</span>
          </h1>
          <p className="text-slate-400 text-sm">
            Analyze agent performance, expenses, and margins by individual agent.
          </p>
        </div>

        {/* Export Button */}
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium"
        >
          <FileDown className="w-4 h-4" />
          Export
        </button>
      </div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
      >
        {summaryCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`
                relative overflow-hidden
                rounded-xl
                bg-slate-800/50
                backdrop-blur-sm
                border ${card.borderColor}
                p-5
                transition-all duration-300
                hover:scale-[1.02]
                hover:shadow-2xl
                hover:bg-slate-800/70
              `}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-50`}
              />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
                      {card.label}
                    </p>
                    <h3 className="text-2xl font-bold text-white">
                      {card.value}
                    </h3>
                  </div>
                  <div className={`${card.iconBg} p-3 rounded-lg`}>
                    <Icon className={`w-5 h-5 ${card.iconColor}`} />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden"
      >
        <div className="p-6 border-b border-slate-700/50">
          <h2 className="text-xl font-semibold text-white">
            Agent Performance Overview
          </h2>
        </div>

        {summaryData.length === 0 ? (
          <div className="text-slate-400 p-8 text-center">No data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900/50">
                  <th className="px-6 py-4 text-left">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Agent Name
                    </span>
                  </th>

                  <th className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleSort("marketing_spent")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Marketing Spent
                      {sortField === "marketing_spent" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleSort("total_calls")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Calls
                      {sortField === "total_calls" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleSort("gateway_cost")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Gateway
                      {sortField === "gateway_cost" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Salary
                    </span>
                  </th>

                  <th className="px-6 py-4 text-center">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Admin
                    </span>
                  </th>

                  {/* Blue-bordered performance section */}
                  <th className="px-6 py-4 text-center bg-blue-500/5 border-l-2 border-blue-500/50">
                    <button
                      onClick={() => handleSort("MCO")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Revenue
                      {sortField === "MCO" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center bg-blue-500/5">
                    <button
                      onClick={() => handleSort("bookings")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Bookings
                      {sortField === "bookings" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center bg-blue-500/5">
                    <button
                      onClick={() => handleSort("conversion")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Conversion %
                      {sortField === "conversion" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center bg-blue-500/5">
                    <button
                      onClick={() => handleSort("revenueRatio")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Revenue Ratio
                      {sortField === "revenueRatio" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center bg-blue-500/5 border-r-2 border-blue-500/50">
                    <button
                      onClick={() => handleSort("gross_margin")}
                      className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-white transition-colors w-full"
                    >
                      Gross Margin
                      {sortField === "gross_margin" && (
                        <span className="text-blue-400">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-700/50">
                {sortedData.map((row, index) => {
                  const isTotal = row.agent_name === "Total";
                  const conversion = row.total_calls > 0
                    ? ((row.bookings / row.total_calls) * 100).toFixed(2)
                    : "0";
                  const revenueRatio = row.marketing_spent > 0
                    ? (row.MCO / row.marketing_spent).toFixed(2)
                    : "0.00";

                  return (
                    <tr
                      key={index}
                      className={`
                        transition-colors duration-150
                        ${
                          isTotal
                            ? "bg-blue-500/10 font-semibold"
                            : "hover:bg-slate-700/30"
                        }
                      `}
                    >
                      <td className="px-6 py-4">
                        <span
                          className={`font-medium ${
                            isTotal ? "text-white" : "text-slate-200"
                          }`}
                        >
                          {row.agent_name}
                        </span>
                      </td>

                      <td
                        className={`px-6 py-4 text-center ${getColorClass(
                          row.marketing_spent,
                          averages.marketing_spent
                        )}`}
                      >
                        ${row.marketing_spent.toFixed(2)}
                        {!isTotal &&
                          getTrendIcon(
                            row.marketing_spent,
                            averages.marketing_spent
                          )}
                      </td>

                      <td className="px-6 py-4 text-center text-slate-300">
                        {row.total_calls}
                      </td>

                      <td
                        className={`px-6 py-4 text-center ${getColorClass(
                          row.gateway_cost,
                          averages.gateway_cost,
                          true
                        )}`}
                      >
                        ${row.gateway_cost.toFixed(2)}
                        {!isTotal &&
                          getTrendIcon(
                            row.gateway_cost,
                            averages.gateway_cost,
                            true
                          )}
                      </td>

                      <td className="px-6 py-4 text-center text-slate-300">
                        ${row.salary_share.toFixed(2)}
                      </td>

                      <td className="px-6 py-4 text-center text-slate-300">
                        ${row.admin_share.toFixed(2)}
                      </td>

                      {/* Blue-bordered performance section */}
                      <td
                        className={`px-6 py-4 text-center bg-blue-500/5 border-l-2 border-blue-500/50 ${getColorClass(
                          row.MCO,
                          averages.MCO
                        )}`}
                      >
                        ${row.MCO.toFixed(2)}
                        {!isTotal && getTrendIcon(row.MCO, averages.MCO)}
                      </td>

                      <td
                        className={`px-6 py-4 text-center bg-blue-500/5 ${getColorClass(
                          row.bookings,
                          averages.bookings
                        )}`}
                      >
                        {row.bookings}
                        {!isTotal &&
                          getTrendIcon(row.bookings, averages.bookings)}
                      </td>

                      <td
                        className={`px-6 py-4 text-center bg-blue-500/5 ${getColorClass(
                          parseFloat(conversion),
                          averages.conversion
                        )}`}
                      >
                        {conversion}%
                        {!isTotal &&
                          getTrendIcon(
                            parseFloat(conversion),
                            averages.conversion
                          )}
                      </td>

                      <td
                        className={`px-6 py-4 text-center bg-blue-500/5 ${getColorClass(
                          parseFloat(revenueRatio),
                          averages.revenueRatio
                        )}`}
                      >
                        {revenueRatio}
                        {!isTotal &&
                          getTrendIcon(
                            parseFloat(revenueRatio),
                            averages.revenueRatio
                          )}
                      </td>

                      <td
                        className={`px-6 py-4 text-center bg-blue-500/5 border-r-2 border-blue-500/50 font-semibold ${
                          row.gross_margin >= 0
                            ? "text-emerald-400"
                            : "text-red-400"
                        }`}
                      >
                        ${row.gross_margin.toFixed(2)}
                        {!isTotal && row.gross_margin >= 0 ? (
                          <TrendingUp className="inline w-3 h-3 ml-1" />
                        ) : !isTotal ? (
                          <TrendingDown className="inline w-3 h-3 ml-1" />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Bottom Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        {[
          {
            label: "Total Gross Margin",
            value: `$${(totals.gross_margin || 0).toFixed(2)}`,
            icon: Wallet,
            positive: totals.gross_margin >= 0,
          },
          {
            label: "Total Gateway Cost",
            value: `$${(totals.gateway_cost || 0).toFixed(2)}`,
            icon: CreditCard,
          },
          {
            label: "Avg Conversion",
            value: `${(
              ((totals.bookings / totals.total_calls) * 100) ||
              0
            ).toFixed(2)}%`,
            icon: TrendingUp,
          },
          {
            label: "Active Agents",
            value: summaryData.filter((r) => r.agent_name !== "Total").length,
            icon: Users,
          },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div
              key={i}
              className="bg-slate-800/30 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4 hover:bg-slate-800/50 transition-colors duration-200"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    stat.positive === false
                      ? "bg-red-500/10"
                      : stat.positive === true
                      ? "bg-emerald-500/10"
                      : "bg-blue-500/10"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      stat.positive === false
                        ? "text-red-400"
                        : stat.positive === true
                        ? "text-emerald-400"
                        : "text-blue-400"
                    }`}
                  />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">
                    {stat.label}
                  </p>
                  <p
                    className={`text-xl font-bold ${
                      stat.positive === false
                        ? "text-red-400"
                        : stat.positive === true
                        ? "text-emerald-400"
                        : "text-white"
                    }`}
                  >
                    {stat.value}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AgentExpenseTable;